import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { ApiErrorResponse, ApiSuccessResponse, AuthState } from "../../@types";
import { logOut, setCredentials } from "../../features/auth/authSlice";

interface RootState {
  auth: AuthState;
}

const apiBase = process.env.REACT_APP_HOBA_APP_API_URL;

const baseQuery = fetchBaseQuery({
  baseUrl: `${apiBase}/api/`,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;

    const token = state.auth?.token;

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// Enhanced base query with proper error handling and token refresh
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Handle authentication errors (401/403)
  if (result?.error?.status === 401 || result?.error?.status === 403) {
    console.log("Authentication failed, attempting token refresh...");

    const refreshResult = await baseQuery(
      {
        url: "/webtokens/refresh",
        method: "POST",
        body: {},
      },
      api,
      extraOptions
    );

    if (refreshResult?.data) {
      const refreshData = refreshResult.data as ApiSuccessResponse<AuthState>;

      // Check if refresh was successful
      if (refreshData.isSuccess && refreshData.data?.token) {
        console.log("Token refresh successful");

        api.dispatch(
          setCredentials({
            accessToken: refreshData?.data?.token,
            user: refreshData?.data?.userDetails,
            permissions: refreshData?.data?.permissions,
          })
        );

        // Retry the original request with new token
        result = await baseQuery(args, api, extraOptions);
      } else {
        console.log("Token refresh failed - invalid response format");
        api.dispatch(logOut());
      }
    } else if (refreshResult?.error) {
      console.log("Token refresh failed:", refreshResult.error);
      api.dispatch(logOut());
      return refreshResult;
    }
  }

  // Handle cases where API returns success: false but with 200 status
  if (result.data) {
    const responseData = result.data as ApiSuccessResponse;

    if (responseData.isSuccess === false) {
      // Convert successful response with isSuccess: false to an error
      const errorResponse: FetchBaseQueryError = {
        status: 400,
        data: {
          messages: responseData.errors || ["Operation failed"],
          source: "API Response",
          exception: responseData.message || "Operation failed",
          errorId: responseData.code || "api-error",
          supportMessage: "Please check your input and try again.",
          statusCode: 400,
        } as ApiErrorResponse,
      };

      return { error: errorResponse };
    }
  }

  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Category", "User", "Product", "Cart", "Order", "Payment"],
  endpoints: (builder) => ({}),
});
